// Members tab — search + role filters + responsive user-card grid + edit/remove.
import { useState, useMemo } from "react";
import { Box, InputBase, Typography, useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import SearchIcon from "@mui/icons-material/Search";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";

import UserCard from "./UserCard";
import EditUserModal from "../modals/EditUserModal";
import RemoveUserModal from "../modals/RemoveUserModal";
import { ROLE_FILTERS } from "../data/mockUsersRoles";
import { saveMemberEdits, removeMember } from "../api";

const MembersTab = ({ users, setUsers, showToast }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editTarget, setEditTarget] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  // BFF call: GET /api/roles/{roleName}/users  → counts for each filter chip (§2.3)
  const counts = useMemo(() => {
    const c = { all: users.length };
    ROLE_FILTERS.forEach((f) => {
      if (f.key !== "all") c[f.key] = users.filter((u) => u.roles.includes(f.key)).length;
    });
    return c;
  }, [users]);

  const filtered = useMemo(() => {
    let list = users;
    if (roleFilter !== "all") list = list.filter((u) => u.roles.includes(roleFilter));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) =>
          u.name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          u.roles.some((r) => r.includes(q))
      );
    }
    return list;
  }, [users, roleFilter, search]);

  // BFF: PUT /api/users/{id} (§2.4) + grant/revoke (§4.4/4.5) + DELETE …/sessions (§2.6)
  const handleSaveEdit = async (updated) => {
    const original = editTarget;
    if (!original) return;
    setSaving(true);
    try {
      const saved = await saveMemberEdits(original, {
        name: updated.name,
        roles: updated.roles,
        projects: updated.projects,
      });
      setUsers((prev) => prev.map((u) => (u.id === saved.id ? saved : u)));
      setEditTarget(null);
      showToast(
        saved.accessChanged
          ? `${saved.name} updated · active sessions ended (re-login required)`
          : `${saved.name} updated`,
        "success"
      );
    } catch (err) {
      console.error("[MembersTab] save failed:", err);
      showToast(`Could not update ${original.name}: ${err.message || "server error"}`, "error");
    } finally {
      setSaving(false);
    }
  };

  // BFF call: DELETE /api/users/{id}  (§2.5) — Keycloak also drops the sessions
  const handleRemove = async (user) => {
    try {
      await removeMember(user.id);
      setUsers((prev) => prev.filter((u) => u.id !== user.id));
      setRemoveTarget(null);
      showToast(`${user.name} removed from the realm`, "success");
    } catch (err) {
      console.error("[MembersTab] remove failed:", err);
      showToast(`Could not remove ${user.name}: ${err.message || "server error"}`, "error");
    }
  };

  return (
    <Box>
      {/* Search + filters row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "var(--fx-paper)",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            px: 1.25,
            height: 36,
            flex: 1,
            minWidth: 220,
            maxWidth: 360,
            "&:focus-within": { borderColor: colors.primary[400] },
          }}
        >
          <SearchIcon sx={{ fontSize: 16, color: colors.grey[500] }} />
          <InputBase
            placeholder="Search by name, email or role…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ fontSize: "0.8125rem", flex: 1, color: 'var(--fx-text-primary)' }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {ROLE_FILTERS.map((f) => {
            const active = roleFilter === f.key;
            return (
              <Box
                key={f.key}
                onClick={() => setRoleFilter(f.key)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.6,
                  cursor: "pointer",
                  userSelect: "none",
                  px: 1.25,
                  height: 30,
                  borderRadius: 1.5,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  transition: "all 0.14s ease",
                  color: active ? "#ffffff" : colors.grey[500],
                  backgroundColor: active ? colors.primary[500] : "var(--fx-paper)",
                  border: `1px solid ${active ? colors.primary[500] : theme.palette.divider}`,
                  "&:hover": active ? {} : { borderColor: colors.primary[300], color: 'var(--fx-text-primary)' },
                }}
              >
                {f.label}
                <Box
                  component="span"
                  sx={{
                    fontSize: "0.625rem",
                    px: 0.5,
                    borderRadius: 1,
                    backgroundColor: active ? "rgba(255,255,255,0.2)" : "rgba(100,116,139,0.12)",
                  }}
                >
                  {counts[f.key] ?? 0}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Grid */}
      {filtered.length ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
            gap: 1.5,
          }}
        >
          {filtered.map((u, i) => (
            <UserCard
              key={u.id}
              user={u}
              index={i}
              onEdit={setEditTarget}
              onRemove={setRemoveTarget}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            color: colors.grey[500],
          }}
        >
          <GroupOutlinedIcon sx={{ fontSize: 36, opacity: 0.4, mb: 1 }} />
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: 'var(--fx-text-primary)' }}>
            No members match
          </Typography>
          <Typography sx={{ fontSize: "0.75rem" }}>Adjust your search or filter</Typography>
        </Box>
      )}

      <EditUserModal
        open={Boolean(editTarget)}
        user={editTarget}
        saving={saving}
        onClose={() => setEditTarget(null)}
        onSave={handleSaveEdit}
      />
      <RemoveUserModal
        open={Boolean(removeTarget)}
        user={removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemove}
      />
    </Box>
  );
};

export default MembersTab;
